import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useCampaign,
  useCampaignStats,
  usePauseCampaign,
  useResumeCampaign,
  useCancelCampaign
} from '../hooks/useCampaigns';
import { useToast } from '../contexts/ToastContext';
import './CampaignStats.css';
import {
  shouldWarnInactiveTemplate,
  INACTIVE_TEMPLATE_MESSAGE
} from '../utils/campaignTemplate';

function CampaignStats() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: campaign, isLoading: campaignLoading } = useCampaign(id);
  const { data: stats, isLoading: statsLoading } = useCampaignStats(id);
  const pauseCampaign = usePauseCampaign(id);
  const resumeCampaign = useResumeCampaign(id);
  const cancelCampaign = useCancelCampaign(id);

  const handlePause = async () => {
    if (!window.confirm('Pause this campaign? Queued emails will be canceled.')) return;

    try {
      await pauseCampaign.mutateAsync();
      toast.success('Campaign paused');
    } catch (error) {
      toast.error(error.message || 'Failed to pause campaign');
    }
  };

  const handleResume = async () => {
    try {
      await resumeCampaign.mutateAsync();
      toast.success('Campaign resumed');
    } catch (error) {
      toast.error(error.message || 'Failed to resume campaign');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this campaign? This cannot be undone.')) return;

    try {
      await cancelCampaign.mutateAsync();
      toast.success('Campaign canceled');
    } catch (error) {
      toast.error(error.message || 'Failed to cancel campaign');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classMap = {
      draft: 'badge-gray',
      scheduled: 'badge-blue',
      sending: 'badge-yellow-pulse',
      sent: 'badge-green',
      paused: 'badge-orange',
      canceled: 'badge-red'
    };
    return `status-badge ${classMap[status] || 'badge-gray'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (campaignLoading || statsLoading) {
    return <div className="campaign-stats-loading">Loading campaign stats...</div>;
  }

  if (!campaign) {
    return <div className="campaign-stats-error">Campaign not found</div>;
  }

  if (campaign.status === 'draft') {
    navigate(`/campaigns/${id}/edit`);
    return null;
  }

  const maxTimelineValue = stats?.timeline
    ? Math.max(...stats.timeline.map(t => t.sent), 1)
    : 1;

  return (
    <div className="campaign-stats">
      <header className="stats-header">
        <div className="header-left">
          <h1>{campaign.name}</h1>
          <span className={getStatusBadgeClass(campaign.status)}>
            {campaign.status}
          </span>
        </div>

        <div className="header-actions">
          {campaign.status === 'sending' && (
            <button className="btn-warning" onClick={handlePause}>
              Pause
            </button>
          )}
          {campaign.status === 'paused' && (
            <button className="btn-primary" onClick={handleResume}>
              Resume
            </button>
          )}
          {(campaign.status === 'sending' || campaign.status === 'paused') && (
            <button className="btn-danger" onClick={handleCancel}>
              Cancel
            </button>
          )}
          <button className="btn-secondary" onClick={() => navigate('/campaigns')}>
            Back to List
          </button>
        </div>
      </header>

      {stats && (
        <>
          <div className="stats-cards">
            <div className="stat-card">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Recipients</div>
            </div>

            <div className="stat-card stat-sent">
              <div className="stat-value">
                {stats.sent}
                <span className="stat-percentage">
                  ({stats.deliveryRate}%)
                </span>
              </div>
              <div className="stat-label">Sent</div>
            </div>

            <div className="stat-card stat-failed">
              <div className="stat-value">{stats.failed}</div>
              <div className="stat-label">Failed</div>
            </div>

            <div className="stat-card stat-queued">
              <div className="stat-value">{stats.queued}</div>
              <div className="stat-label">Queued</div>
            </div>
          </div>

          {stats.timeline && stats.timeline.length > 0 && (
            <div className="timeline-section">
              <h2>Delivery Timeline</h2>
              <div className="timeline-chart">
                {stats.timeline.map((entry, idx) => (
                  <div key={idx} className="timeline-bar-container">
                    <div className="timeline-hour">
                      {formatDate(entry.hour)}
                    </div>
                    <div className="timeline-bar-wrapper">
                      <div
                        className="timeline-bar"
                        style={{
                          width: `${(entry.sent / maxTimelineValue) * 100}%`
                        }}
                      />
                    </div>
                    <div className="timeline-count">{entry.sent}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="campaign-details">
            <h2>Campaign Details</h2>
            <dl className="details-list">
              <dt>Template</dt>
              <dd>
                {campaign.template_name}
                {shouldWarnInactiveTemplate(campaign) && (
                  <span className="template-inactive" title={INACTIVE_TEMPLATE_MESSAGE}>
                    template inactive
                  </span>
                )}
              </dd>

              <dt>Created</dt>
              <dd>{formatDate(campaign.created_at)}</dd>

              {campaign.schedule_at && (
                <>
                  <dt>Scheduled For</dt>
                  <dd>{formatDate(campaign.schedule_at)}</dd>
                </>
              )}

              {campaign.frequency_cap_days > 0 && (
                <>
                  <dt>Frequency Cap</dt>
                  <dd>{campaign.frequency_cap_days} days</dd>
                </>
              )}

              {(campaign.utm_source || campaign.utm_medium || campaign.utm_campaign) && (
                <>
                  <dt>UTM Parameters</dt>
                  <dd className="utm-params">
                    source={campaign.utm_source || 'email'},
                    medium={campaign.utm_medium || 'campaign'},
                    campaign={campaign.utm_campaign || 'campaign'}
                  </dd>
                </>
              )}
            </dl>
          </div>
        </>
      )}
    </div>
  );
}

export default CampaignStats;
